import { useState } from "react";
import { Flag, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Reveal } from "@/components/landing/Reveal";

export const HonorSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", item: "U.S. flag" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/honor-claims", { name: form.name, phone: form.phone, item: form.item });
      setDone(true);
      toast.success("Thank you for your service — we'll call to arrange your free cleaning.");
    } catch {
      toast.error("Something went wrong — please call 480-940-6023.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="honor" data-testid="honor-section" className="grain relative overflow-hidden bg-brand-navy py-24 text-white lg:py-32">
      <img
        src="/images/veterans_flag.jpg"
        alt=""
        aria-hidden
        onError={(e) => (e.currentTarget.style.display = "none")}
        className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15"
      />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-brand-navy/80 via-brand-navy/60 to-brand-navy" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(37,99,235,0.2),transparent_55%),radial-gradient(circle_at_85%_85%,rgba(245,158,11,0.08),transparent_50%)]" />

      <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-amber-300 sm:text-sm">
            <Star className="h-4 w-4 fill-fall-amber text-fall-amber" /> Our standing promise
          </p>
          <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Class A uniforms &amp; U.S. flags — <span className="text-fall-amber">cleaned free, always.</span>
          </h2>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
            If you served, your Class A uniform and your American flag are cleaned and pressed free of
            charge — no purchase, no fine print, no expiration. It&apos;s our family&apos;s way of saying
            thank you to fellow veterans, service members, and first responders across the East Valley.
          </p>
          <ul className="mt-6 space-y-3 text-sm font-semibold text-slate-200">
            {[
              "Dress uniforms cleaned & pressed to inspection standard",
              "American flags gently cleaned and properly folded",
              "Free pickup & delivery, like every Oxford order",
            ].map((line) => (
              <li key={line} className="flex items-start gap-2.5">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" /> {line}
              </li>
            ))}
          </ul>

          <div className="mt-8 flex items-center gap-4 rounded-2xl border border-amber-300/25 bg-white/5 p-4 backdrop-blur">
            <img
              data-testid="honor-owner-photo"
              src="/images/owner_military.jpg"
              alt="Oxford Cleaners owner in U.S. military dress uniform"
              className="h-24 w-20 shrink-0 rounded-xl border-2 border-fall-amber object-cover object-top shadow-lg"
            />
            <div>
              <p className="font-display text-base font-bold text-white">Wais Kushkaki — proud veteran &amp; owner</p>
              <p className="mt-1 text-sm leading-relaxed text-slate-300">
                &ldquo;I served my country — now I serve my neighbors. Class A uniforms and American
                flags are cleaned free, always. That&apos;s how we give back.&rdquo;
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="overflow-hidden rounded-[2rem] border border-white/15 shadow-2xl shadow-black/40">
            <img
              data-testid="honor-flag-image"
              src="/images/honor_flag.png"
              onError={(e) => (e.currentTarget.style.display = "none")}
              alt="Ceremonially folded American flag beside a pressed dress uniform"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>

          <div className="mt-6 rounded-3xl border border-amber-300/25 bg-white/5 p-6 backdrop-blur">
            {done ? (
              <div data-testid="honor-success" className="flex items-center gap-3 text-emerald-400">
                <CheckCircle2 className="h-6 w-6 shrink-0" />
                <p className="text-sm font-semibold">
                  Request received — we&apos;ll call {form.phone} to arrange your free cleaning.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                <input
                  data-testid="honor-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                />
                <input
                  data-testid="honor-phone-input"
                  required
                  minLength={7}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone number"
                  className="rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold placeholder:text-slate-400 focus:border-fall-amber focus:outline-none"
                />
                <div className="flex gap-3 sm:col-span-3">
                  <select
                    data-testid="honor-item-select"
                    value={form.item}
                    onChange={(e) => setForm({ ...form, item: e.target.value })}
                    className="flex-1 rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-semibold text-white focus:border-fall-amber focus:outline-none [&>option]:text-brand-navy"
                  >
                    <option>U.S. flag</option>
                    <option>Class A uniform</option>
                    <option>Both</option>
                  </select>
                  <button
                    data-testid="honor-submit-button"
                    type="submit"
                    disabled={sending}
                    className="flex items-center gap-2 rounded-xl bg-fall-amber px-6 py-3 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy transition-transform hover:scale-[1.03] disabled:opacity-60"
                  >
                    <Flag className="h-4 w-4" /> {sending ? "Sending…" : "Claim free cleaning"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
